/**
 * Quick View Modal — Hermes Music
 * Galería propia con crossfade + scale, sin Swiper
 */

const QuickViewModal = (function () {
  'use strict';

  // DOM
  let modal, stageEl, thumbsEl, bodyEl, prevBtn, nextBtn, counterEl;

  // Galería
  let imgs = [];
  let current = 0;
  let busy = false;

  // Modal state
  let isOpen = false;
  let prevFocus = null;

  // ─── Init ────────────────────────────────────────────

  function cache() {
    modal     = document.getElementById('modal_quickView');
    if (!modal) return false;
    stageEl   = modal.querySelector('[data-qv-stage]');
    thumbsEl  = modal.querySelector('[data-qv-thumbs]');
    bodyEl    = modal.querySelector('[data-quickview-body]');
    prevBtn   = modal.querySelector('[data-qv-prev]');
    nextBtn   = modal.querySelector('[data-qv-next]');
    counterEl = modal.querySelector('[data-qv-counter]');
    return true;
  }

  function init() {
    if (!cache()) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', cache);
      }
      return;
    }

    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-quickview-close]')) close();
    });

    prevBtn.addEventListener('click', () => goTo(current - 1));
    nextBtn.addEventListener('click', () => goTo(current + 1));

    document.addEventListener('keydown', (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape')     { e.preventDefault(); close(); }
      if (e.key === 'ArrowLeft')  goTo(current - 1);
      if (e.key === 'ArrowRight') goTo(current + 1);
    });

    bindTouch();
  }

  function bindTouch() {
    let startX = 0;
    stageEl.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });
    stageEl.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 48) goTo(current + (dx < 0 ? 1 : -1));
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ─── Gallery ─────────────────────────────────────────

  function buildGallery(images, title) {
    imgs = images || [];
    current = 0;
    busy = false;
    stageEl.innerHTML = '';
    thumbsEl.innerHTML = '';

    if (imgs.length === 0) {
      stageEl.innerHTML = `
        <div class="qv-gallery__empty">
          <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
        </div>`;
      prevBtn.hidden = true;
      nextBtn.hidden = true;
      counterEl.hidden = true;
      return;
    }

    imgs.forEach((src, i) => {
      // Imagen principal
      const img = document.createElement('img');
      img.src = src;
      img.alt = `${title} — imagen ${i + 1}`;
      img.loading = i === 0 ? 'eager' : 'lazy';
      img.className = 'qv-gallery__img' + (i === 0 ? ' is-active' : '');
      img.draggable = false;
      stageEl.appendChild(img);

      // Thumbnail
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'qv-thumbs__btn' + (i === 0 ? ' is-active' : '');
      btn.dataset.index = i;
      btn.setAttribute('aria-label', `Ver imagen ${i + 1}`);
      const tImg = document.createElement('img');
      tImg.src = src;
      tImg.alt = '';
      tImg.loading = 'lazy';
      btn.appendChild(tImg);
      btn.addEventListener('click', () => goTo(i));
      thumbsEl.appendChild(btn);
    });

    syncNav();
    syncCounter();
  }

  function goTo(index) {
    if (busy || index === current || index < 0 || index >= imgs.length) return;

    const dir = index > current ? 'fwd' : 'bwd';
    const allImgs   = stageEl.querySelectorAll('.qv-gallery__img');
    const allThumbs = thumbsEl.querySelectorAll('.qv-thumbs__btn');

    const outImg = allImgs[current];
    const inImg  = allImgs[index];

    busy = true;

    // 1. Marcar saliente
    outImg.classList.add(`is-leaving-${dir}`);

    // 2. Preparar entrante (fuera de vista)
    inImg.classList.add(`is-entering-${dir}`);

    // 3. Forzar reflow para que el browser registre el estado inicial
    void inImg.offsetWidth;

    // 4. Activar entrante (dispara transition CSS)
    inImg.classList.remove(`is-entering-${dir}`);
    inImg.classList.add('is-active');

    // 5. Limpiar clases después de la transición
    const duration = parseFloat(
      getComputedStyle(document.documentElement)
        .getPropertyValue('--qv-duration') || '0.46'
    ) * 1000 + 40;

    setTimeout(() => {
      outImg.classList.remove('is-active', `is-leaving-${dir}`);
      busy = false;
    }, duration);

    // Thumbs
    allThumbs[current].classList.remove('is-active');
    allThumbs[index].classList.add('is-active');
    allThumbs[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

    current = index;
    syncNav();
    syncCounter();
  }

  function syncNav() {
    const solo = imgs.length <= 1;
    prevBtn.hidden = solo;
    nextBtn.hidden = solo;
    if (!solo) {
      prevBtn.disabled = current === 0;
      nextBtn.disabled = current === imgs.length - 1;
    }
  }

  function syncCounter() {
    if (imgs.length <= 1) { counterEl.hidden = true; return; }
    counterEl.hidden = false;
    counterEl.textContent = `${current + 1} / ${imgs.length}`;
  }

  // ─── Producto ────────────────────────────────────────

  function formatPrice(cents) {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(cents / 100);
  }

  function buildPriceHTML(price, compare) {
    if (compare && compare > price) {
      return `
        <div class="price price--on-sale">
          <div class="price__sale">
            <s class="price-item price-item--regular">${formatPrice(compare)}</s>
            <span class="price-item price-item--sale">${formatPrice(price)}</span>
          </div>
        </div>`;
    }
    return `
      <div class="price">
        <span class="price-item price-item--regular">${formatPrice(price)}</span>
      </div>`;
  }

  function renderProduct(product) {
    const modeloTag = product.tags?.find(t => t.toLowerCase().includes('modelo'));
    const modelo = modeloTag ? (modeloTag.split('_')[1] || modeloTag) : null;

    bodyEl.innerHTML = `
      <div class="qv-product">
        ${product.vendor ? `
          <div class="qv-product__vendor">
            <a href="/collections/vendors?q=${encodeURIComponent(product.vendor)}">
              ${product.vendor}
            </a>
          </div>` : ''}

        <h2 class="qv-product__title" id="qv-title">${product.title}</h2>

        <div class="qv-product__meta">
          ${modelo ? `<span><strong>Modelo:</strong> ${modelo}</span>` : ''}
          ${product.variants[0]?.sku ? `<span class="qv-product__sku">SKU: ${product.variants[0].sku}</span>` : ''}
        </div>

        <div class="qv-product__price">
          ${buildPriceHTML(product.price, product.compare_at_price)}
        </div>

        <a href="${product.url}" class="qv-product__cta">
          Ver producto completo
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"/>
            <polyline points="12 5 19 12 12 19"/>
          </svg>
        </a>

        ${product.description ? `
          <div class="qv-product__desc-wrap">
            <div class="qv-product__desc">${product.description}</div>
          </div>
          <a href="${product.url}" class="qv-product__desc-link">
            Ver descripción completa
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </a>` : ''}
      </div>`;

    injectRewards(product);
  }

  function injectRewards(product) {
    const src = document.getElementById('qv-rewards-src');
    if (!src) return;
    const srcEl = src.querySelector('[data-qv-rewards]');
    if (!srcEl) return;

    const rewardsEl = srcEl.cloneNode(true);
    const ptEl = rewardsEl.querySelector('[data-qv-points]');
    if (ptEl) {
      const multiplier = parseFloat(rewardsEl.dataset.multiplier) || 0;
      const points = Math.round((product.price / 100) * multiplier / 100);
      ptEl.textContent = `~${points}`;
    }

    const cta = bodyEl.querySelector('.qv-product__cta');
    if (cta) cta.parentNode.insertBefore(rewardsEl, cta);
  }

  function renderError() {
    bodyEl.innerHTML = `
      <div class="qv-product__error">
        <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <p>No se pudo cargar el producto.<br>Intenta de nuevo.</p>
      </div>`;
  }

  function showLoading() {
    stageEl.innerHTML = '';
    thumbsEl.innerHTML = '';
    prevBtn.hidden = true;
    nextBtn.hidden = true;
    counterEl.hidden = true;
    bodyEl.innerHTML = `
      <div class="qv-loading">
        <div class="qv-spinner"></div>
        <span>Cargando producto…</span>
      </div>`;
  }

  // ─── Fetch ───────────────────────────────────────────

  async function loadProduct(handle) {
    showLoading();
    try {
      const res = await fetch(`${window.Shopify.routes.root}products/${handle}.js`);
      if (!res.ok) throw new Error(res.status);
      const product = await res.json();
      buildGallery(product.images, product.title);
      renderProduct(product);
    } catch (err) {
      console.error('[QuickView] Error:', err);
      renderError();
    }
  }

  // ─── Open / Close ─────────────────────────────────────

  function open(handle, e) {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!modal && !cache()) return;

    prevFocus = document.activeElement;
    modal.hidden = false;
    isOpen = true;
    document.body.style.overflow = 'hidden';

    const closeBtn = modal.querySelector('.qv-modal__close');
    if (closeBtn) setTimeout(() => closeBtn.focus(), 80);

    loadProduct(handle);
  }

  function close() {
    if (!modal || !isOpen) return;
    modal.hidden = true;
    isOpen = false;
    document.body.style.overflow = '';

    setTimeout(() => {
      if (stageEl)  stageEl.innerHTML = '';
      if (thumbsEl) thumbsEl.innerHTML = '';
      if (bodyEl)   bodyEl.innerHTML = '';
      imgs = [];
      current = 0;
      busy = false;
    }, 300);

    if (prevFocus) { prevFocus.focus(); prevFocus = null; }
  }

  // ─── API pública ─────────────────────────────────────

  return {
    open,
    close,
    isOpen: () => isOpen,
  };
})();

// Retrocompatibilidad
function quickView(handle, event) { QuickViewModal.open(handle, event); }
window.quickView = quickView;
window.QuickViewModal = QuickViewModal;
